import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon,
  ArrowReloadHorizontalIcon,
  Invoice03Icon,
  Link03Icon,
  MagicWand04Icon,
  MoneyAdd01Icon,
  MoneySend02Icon,
  TransactionHistoryIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { TableRowActions, toast } from "@outpave/ds";
import { useFeatureEntitlements } from "@frontegg/react";
import ApplicationType from "enums/ApplicationType";
import { BankAccount } from "hooks/useGetBankAccounts";
import { AccountDrawerRequest } from "./AccountActionsMenu";

interface AccountRowActionsProps {
  account: BankAccount;
  onRequestDrawer: (request: AccountDrawerRequest) => void;
}

/**
 * The actions for one card program, for a panel listing several. The drawers
 * themselves stay with the panel's single AccountActionsMenu; this only asks
 * for one, scoped to its own row.
 */
const AccountRowActions: React.FC<AccountRowActionsProps> = ({
  account,
  onRequestDrawer,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isEntitled: featureManageBanks } = useFeatureEntitlements(
    "feature_manage_banks",
  );

  const isCharge = account.applicationType === ApplicationType.CHARGE;
  // The program answers for its issuer and the company's plan together.
  const capabilities = account.cardProgram?.capabilities;

  const requests = (drawer: AccountDrawerRequest["drawer"]) => () =>
    onRequestDrawer({ drawer, accountId: account.id });

  const comingSoon = (feature: string) => () =>
    toast.info(t("{{feature}} is coming soon", { feature }));

  const prepaidActions = [
    {
      label: t("Add Funds"),
      icon: <HugeiconsIcon icon={MoneyAdd01Icon} size={18} />,
      onClick: requests("addFunds"),
      shouldNotShow: !capabilities?.moveFunds,
    },
    {
      label: t("Transfer Funds"),
      icon: <HugeiconsIcon icon={ArrowDataTransferHorizontalIcon} size={18} />,
      onClick: requests("transferFunds"),
      shouldNotShow: !capabilities?.moveFunds,
    },
    {
      label: t("Auto Balance"),
      icon: <HugeiconsIcon icon={MagicWand04Icon} size={18} />,
      onClick: requests("autoBalance"),
      shouldNotShow: !capabilities?.moveFunds,
    },
  ];

  const chargeActions = [
    {
      label: t("Pay Now"),
      icon: <HugeiconsIcon icon={MoneySend02Icon} size={18} />,
      onClick: requests("repay"),
      shouldNotShow: !capabilities?.chargeRepayment,
    },
    {
      label: t("Auto Pay"),
      icon: <HugeiconsIcon icon={ArrowReloadHorizontalIcon} size={18} />,
      onClick: comingSoon(t("Auto Pay")),
      shouldNotShow: false,
    },
  ];

  const actions = [
    {
      label: t("View activity"),
      icon: <HugeiconsIcon icon={TransactionHistoryIcon} size={18} />,
      onClick: () =>
        navigate("/admin/transactions", { state: { accountId: account.id } }),
      shouldNotShow: false,
    },
    ...(isCharge ? chargeActions : prepaidActions),
    // Prepaid programs get monthly statements as well as charge ones.
    {
      label: t("Statements"),
      icon: <HugeiconsIcon icon={Invoice03Icon} size={18} />,
      onClick: () =>
        navigate("/admin/statements", { state: { accountId: account.id } }),
      shouldNotShow: !capabilities?.statements,
    },
    {
      label: t("Connected Accounts"),
      icon: <HugeiconsIcon icon={Link03Icon} size={18} />,
      onClick: requests("connectedAccounts"),
      shouldNotShow: !featureManageBanks,
    },
  ].filter((action) => !action.shouldNotShow);

  return (
    <TableRowActions
      actions={actions}
      label={t("Account actions for {{name}}", {
        name: account.nickname || account.bankName || "",
      })}
    />
  );
};

export default AccountRowActions;
